// Şube Yönetici İzinleri penceresi kullanım rehberi. Metinler 'guides' çeviri alanında.
import type { TFunction } from 'i18next';
import type { GuideContent } from '../../components/common/UsageGuideDialog';

const G = 'branches.managerPermissions';

export const branchesManagerPermissionsGuide = (t: TFunction): GuideContent => ({
  layout: 'dialog',
  title: t(`${G}.title`, 'Şube yöneticisi izinleri nasıl verilir?'),
  intro: t(`${G}.intro`, 'Şube yöneticisinin kendi panelinde hangi menüleri göreceğini ve hangi işlemleri yapabileceğini belirlersiniz. İzin vermediğiniz modüller yönetici panelinde görünmez. Haritada bir bölgeye tıklayarak o adıma geçebilirsiniz.'),
  regionLabels: {
    header: t(`${G}.regions.header`, 'Başlık'),
    body: t(`${G}.regions.body`, 'Yönetici seçimi ve modül izinleri'),
    footer: t(`${G}.regions.footer`, 'İptal / İzinleri Kaydet'),
  },
  steps: [
    {
      region: 'body',
      title: t(`${G}.s1.title`, '1. Yöneticiyi seçin'),
      text: t(`${G}.s1.text`, '"Yönetici Seçin" listesinde bu şubenin yöneticileri çıkar: şubesi bu şube olan yöneticiler ve bu şubeye ek olarak atananlar ("Ek atama" etiketli). Şubede tek yönetici varsa otomatik seçilir. Seçince o yöneticinin kayıtlı izinleri yüklenir.'),
      tips: [
        t(`${G}.s1.tip1`, '"Şubede henüz yönetici bulunmuyor" yazıyorsa önce bu şubeye bir yönetici tanımlayın.'),
        t(`${G}.s1.tip2`, 'Hiç izin kaydedilmemiş bir yöneticinin panelinde yalnız Dashboard görünür; kutular boş gelir, ilk kayıtla seçtikleriniz geçerli olur.'),
      ],
    },
    {
      region: 'body',
      title: t(`${G}.s2.title`, '2. Grupları açın'),
      text: t(`${G}.s2.text`, '"Modül İzinleri" kenar çubuğundaki başlıklarla aynı gruplara ayrılmıştır: Ön Muhasebe, Satış Sistemi, Görev Takip, İnsan Kaynakları ve diğerleri. Grup başlığına tıklayınca satırları açılır; başlıktaki sayı o grupta açık olan izin sayısını gösterir.'),
      tips: [
        t(`${G}.s2.tip1`, '"Yakında" etiketli soluk satırlar henüz şube yöneticisine açılamayan menülerdir; seçilemez.'),
        t(`${G}.s2.tip2`, 'Üretim satırları yalnız şirketinizde Üretim modülü açıksa görünür.'),
      ],
    },
    {
      region: 'body',
      title: t(`${G}.s3.title`, '3. Modül ve alt izin satırları'),
      text: t(`${G}.s3.text`, 'Modül satırındaki kutu o menüyü yöneticiye açar. Altındaki girintili satırlar o modülde yapabileceği işlemlerdir (ör. Kasa › Kasadan gider ekle, Banka › Para yatır / çek, Personel › Personel hareketleri). Bir alt izni seçerseniz modül de açılır; modülü kapatırsanız alt izinleri de kapanır.'),
      tips: [
        t(`${G}.s3.tip1`, 'Görüntüleme ile işlem farkı: modül satırı yalnız açıklamasında yazanlara izin verir (ör. Kasa: kasaları listeleme ve yeni kasa ekleme). Kasadan gider, para yatır / çek, miktar düzeltme gibi işlemler için ilgili alt satırı da işaretleyin.'),
        t(`${G}.s3.tip2`, 'Bağlı izinler: Alış Faturası Ekleme / Düzenleme / Silme için önce "Faturalar", Depo › Miktar düzelt ve Stok hareketleri için önce "Görüntüle" açık olmalı. Üst izni kapatırsanız bağlı izinler de kapanır; Depo › Görüntüle kapanırsa Depo modülü de kapanır.'),
        t(`${G}.s3.tip3`, 'Satır açıklamaları sınırı söyler: yönetici yalnız kendi şubesinin cari, kasa, banka ve depo kayıtlarıyla çalışır; Kâr-Zarar Raporunda merkez giderleri görünmez.'),
        t(`${G}.s3.tip4`, 'Üretim › "Sabit üretim yapabilir": yönetici serbest fiş açmadan yalnız tanımlı sabit üretimleri yapar (Üretim › Fişler › Sabit Üretimler sekmesi). Fiş oluşturma ve fiş tamamlama izni olan yönetici bu izin olmadan da sabit üretim yapabilir.'),
      ],
    },
    {
      region: 'body',
      title: t(`${G}.s4.title`, '4. Açılır alt izinler'),
      text: t(`${G}.s4.text`, 'Dashboard, QR Menü ve Satış Raporları satırlarındaki ok "Alt İzinler" panelini açar (ör. Dashboard\'da hangi grafiklerin görüneceği). Modül açıkken "Tümünü Seç" ve "Tümünü Temizle" düğmeleri çıkar; modül kapalıyken alt kutular seçilemez.'),
    },
    {
      region: 'body',
      title: t(`${G}.s5.title`, '5. Siparişler (B2B)'),
      text: t(`${G}.s5.text`, 'Şube bir B2B satıcıya giriş yaptıysa "Siparişler" satırı "B2B girişi ile açık" etiketiyle kilitli ve işaretli gelir; kapatılamaz ve şube panelinde her zaman görünür.'),
    },
    {
      region: 'footer',
      title: t(`${G}.s6.title`, '6. İzinleri kaydedin'),
      text: t(`${G}.s6.text`, '"İzinleri Kaydet" seçili yöneticinin izinlerini kaydeder; başarı mesajından sonra pencere kapanır. İzin verilmeyen modüller yönetici panelinde görünmez ve sunucu da bu izinleri denetler; yönetici izni olmayan işlemi yapamaz.'),
      tips: [
        t(`${G}.s6.tip1`, 'Yanlış izin, yöneticinin görmemesi gereken verilere ulaşmasına ya da işini yapamamasına yol açar; kaydetmeden önce açık satırları gözden geçirin.'),
        t(`${G}.s6.tip2`, '"İptal" değişiklikleri kaydetmeden kapatır.'),
      ],
    },
  ],
});
